import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';

export default function StatsPanel() {
  const { currentTeam, userProfile } = useApp();
  const [playerStats, setPlayerStats] = useState(null);
  const [teamRankings, setTeamRankings] = useState([]);
  const [rankCategory, setRankCategory] = useState('points');
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (currentTeam) loadStats(); }, [currentTeam]);

  const loadStats = async () => {
    setLoading(true);
    const userId = auth.currentUser?.uid;
    if (!userId || !currentTeam) { setLoading(false); return; }

    try {
      // 1) playerStats 컬렉션에서 먼저 조회
      const statsQ = query(
        collection(db, 'playerStats'),
        where('userId', '==', userId),
        where('teamId', '==', currentTeam.id),
        limit(1)
      );
      const statsSnap = await getDocs(statsQ);

      if (statsSnap.docs.length > 0) {
        setPlayerStats({ id: statsSnap.docs[0].id, ...statsSnap.docs[0].data() });
      } else {
        // 2) gameRecords에서 직접 계산 (iOS calculatePlayerStats 로직)
        const recordsQ = query(
          collection(db, 'gameRecords'),
          where('userId', '==', userId),
          where('teamId', '==', currentTeam.id),
          where('approvalStatus', '==', 'approved')
        );
        const recordsSnap = await getDocs(recordsQ);
        const records = recordsSnap.docs.map(d => d.data());

        const goals = records.filter(r => r.recordType === 'goal').length;
        const assists = records.filter(r => r.recordType === 'assist').length;
        const saves = records.filter(r => r.recordType === 'save').length;
        const scheduleIds = [...new Set(records.map(r => r.scheduleId))];

        // 쿼터별 득점
        const goalsByQuarter = {};
        records.filter(r => r.recordType === 'goal').forEach(r => {
          const q = r.quarter || 0;
          goalsByQuarter[q] = (goalsByQuarter[q] || 0) + 1;
        });

        setPlayerStats({
          goals, assists, points: goals + assists, saves,
          gamesPlayed: scheduleIds.length,
          goalsPerGame: scheduleIds.length > 0 ? (goals / scheduleIds.length).toFixed(2) : '0',
          assistsPerGame: scheduleIds.length > 0 ? (assists / scheduleIds.length).toFixed(2) : '0',
          pointsPerGame: scheduleIds.length > 0 ? ((goals + assists) / scheduleIds.length).toFixed(2) : '0',
          goalsByQuarter,
        });
      }

      // 팀 랭킹 (모든 플레이어의 통계)
      const allStatsQ = query(
        collection(db, 'playerStats'),
        where('teamId', '==', currentTeam.id)
      );
      const allSnap = await getDocs(allStatsQ);
      const allStats = allSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeamRankings(allStats);

    } catch (err) { console.error('Stats load error:', err); }
    setLoading(false);
  };

  const sortedRankings = [...teamRankings].sort((a, b) => {
    switch (rankCategory) {
      case 'goals': return (b.goals || 0) - (a.goals || 0);
      case 'assists': return (b.assists || 0) - (a.assists || 0);
      case 'points': return ((b.goals || 0) + (b.assists || 0)) - ((a.goals || 0) + (a.assists || 0));
      default: return 0;
    }
  });

  if (loading) return <div style={s.empty}>통계 불러오는 중...</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 1200 }}>
      {/* 개인 통계 */}
      <div style={s.panel}>
        <div style={s.ph}><h2 style={s.pt}>📊 내 기록</h2></div>
        <div style={{ padding: 20 }}>
          {playerStats ? (
            <>
              {/* 포인트 대형 표시 */}
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 56, fontWeight: 700, color: '#5BC4D8' }}>{playerStats.points || 0}</div>
                <div style={{ fontSize: 12, color: '#778899', letterSpacing: 2 }}>POINTS</div>
              </div>

              {/* 주요 통계 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
                <BigStat value={playerStats.goals || 0} label="득점" color="#F59E0B" />
                <BigStat value={playerStats.assists || 0} label="어시스트" color="#3B82F6" />
                <BigStat value={playerStats.gamesPlayed || 0} label="출전" color="#8B5CF6" />
              </div>

              {/* 세부 통계 */}
              <div style={{ borderTop: '1px solid rgba(91,196,216,0.06)', paddingTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#5BC4D8', marginBottom: 12 }}>경기당 평균</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <SmallStat label="득점/경기" value={playerStats.goalsPerGame || '0'} />
                  <SmallStat label="어시스트/경기" value={playerStats.assistsPerGame || '0'} />
                  <SmallStat label="포인트/경기" value={playerStats.pointsPerGame || '0'} />
                </div>
              </div>

              {/* 쿼터별 득점 분포 */}
              {playerStats.goalsByQuarter && Object.keys(playerStats.goalsByQuarter).length > 0 && (
                <div style={{ borderTop: '1px solid rgba(91,196,216,0.06)', paddingTop: 16, marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5BC4D8', marginBottom: 12 }}>쿼터별 득점</div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {[1, 2, 3, 4].map(q => {
                      const count = playerStats.goalsByQuarter[q] || 0;
                      const max = Math.max(...Object.values(playerStats.goalsByQuarter), 1);
                      return (
                        <div key={q} style={{ flex: 1, textAlign: 'center' }}>
                          <div style={{ height: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: 4 }}>
                            <div style={{ width: '70%', height: Math.max((count / max) * 60, 4), backgroundColor: '#5BC4D8', borderRadius: '4px 4px 0 0', transition: 'height 0.3s' }} />
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{count}</div>
                          <div style={{ fontSize: 11, color: '#778899' }}>{q === 4 ? 'OT' : q + 'Q'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 워치 통계 */}
              {playerStats.watchStats && (
                <div style={{ borderTop: '1px solid rgba(91,196,216,0.06)', paddingTop: 16, marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#4ADE80', marginBottom: 12 }}>⌚ 워치 연동 통계</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <SmallStat label="평균 심박수" value={(playerStats.watchStats.averageHeartRate || '-') + ' bpm'} />
                    <SmallStat label="최대 심박수" value={(playerStats.watchStats.maxHeartRate || '-') + ' bpm'} />
                    <SmallStat label="기록 세션" value={playerStats.watchStats.sessionsRecorded || 0} />
                    <SmallStat label="총 빙판 시간" value={formatIceTime(playerStats.watchStats.totalIceTimeFromWatch)} />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={s.empty}>아직 기록된 통계가 없습니다</div>
          )}
        </div>
      </div>

      {/* 팀 랭킹 */}
      <div style={s.panel}>
        <div style={{ ...s.ph, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={s.pt}>🏆 팀 랭킹</h2>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['points', '포인트'], ['goals', '득점'], ['assists', '어시스트']].map(([k, l]) => (
              <button key={k} onClick={() => setRankCategory(k)} style={{
                padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                backgroundColor: rankCategory === k ? 'rgba(91,196,216,0.15)' : 'transparent', color: rankCategory === k ? '#5BC4D8' : '#667788',
              }}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ padding: 12 }}>
          {sortedRankings.length === 0 ? (
            <div style={s.empty}>팀 랭킹 데이터가 없습니다<br /><span style={{ fontSize: 12 }}>playerStats 컬렉션에 데이터가 쌓이면 표시됩니다</span></div>
          ) : sortedRankings.map((p, i) => {
            const val = rankCategory === 'goals' ? (p.goals || 0) : rankCategory === 'assists' ? (p.assists || 0) : ((p.goals || 0) + (p.assists || 0));
            const isMe = p.userId === auth.currentUser?.uid;
            return (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', marginBottom: 4, borderRadius: 8,
                backgroundColor: isMe ? 'rgba(91,196,216,0.06)' : 'transparent',
              }}>
                <span style={{ width: 28, textAlign: 'center', fontSize: i < 3 ? 18 : 13, fontWeight: 700, color: i === 0 ? '#FBBF24' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#778899' }}>
                  {i < 3 ? ['🥇', '🥈', '🥉'][i] : (i + 1)}
                </span>
                <div style={s.avatar}>{(p.userName || '?')[0].toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: isMe ? 700 : 500, color: isMe ? '#5BC4D8' : '#E8ECF1' }}>{p.userName || 'Unknown'}</div>
                </div>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#5BC4D8' }}>{val}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BigStat({ value, label, color }) {
  return (
    <div style={{ textAlign: 'center', padding: 12, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#778899', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div style={{ padding: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize: 11, color: '#667788', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function formatIceTime(seconds) {
  if (!seconds) return '--:--';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (min >= 60) return Math.floor(min / 60) + 'h ' + (min % 60) + 'm';
  return min + ':' + String(sec).padStart(2, '0');
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  ph: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)' },
  pt: { margin: 0, fontSize: 15, fontWeight: 600 },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  avatar: { width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(91,196,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#5BC4D8' },
};
